/**
 * Analytics types for GenAI Studio v2
 */

// Screen action types
export enum AiGenScreenAction {
  GENERATE = 'generate',
  GENERATE_CLICK = 'generate_click',
  RETRY = 'retry',
  DISMISS_ERROR = 'dismiss_error',
  UPLOAD = 'upload',
  UPLOAD_CLICK = 'upload_click',
  UPLOAD_FAILURE = 'upload_failure',
  DOWNLOAD = 'download',
  CANCEL = 'cancel',
  EDIT = 'edit',
  BACK = 'back',
  CLICK = 'click',
  SHARE = 'share',
  COPY_LINK = 'copy_link',
  DELETE = 'delete',
  REPORT = 'report',
  REUSE_TEMPLATE = 'reuse_template',
  LIKE = 'like',
  DISLIKE = 'dislike',
  PRESET_LIKE = 'preset_like',
  PRESET_UNLIKE = 'preset_unlike',
  TRENDING_ITEM_CLICK = 'trending_item_click',
  CHIP_CLICK = 'chip_click',
  SEE_ALL = 'see_all',
  OPEN = 'feed_open',
  SWIPE_UP = 'swipe_up',
  SWIPE_DOWN = 'swipe_down',
  PROFILE_OPEN = 'profile_open',
  CREDITS_OPEN = 'credits_open',
  GET_MORE = 'get_more',
  SAVE = 'save',
  UNSAVE = 'unsave',
  STREAK_CLICK = 'streak_click',
  STREAK_AUTO_OPEN = 'streak_auto_open',
  STREAK_SHEET_OPEN = 'streak_sheet_open',
  STREAK_SHEET_CLOSE = 'streak_sheet_close',
  STREAK_CLAIM = 'streak_claim',
  STREAK_CLAIM_EARLY = 'streak_claim_early',
  STREAK_CLAIM_FAIL = 'streak_claim_fail',
  STREAK_COMPLETE = 'streak_complete',
  STREAK_GUEST_SHEET_OPEN = 'streak_guest_sheet_open',
  STREAK_GUEST_CLAIM_CLICK = 'streak_guest_claim_click',
  STREAK_GUEST_REGISTRATION_OPEN = 'streak_guest_registration_open',
  SEARCH_OPEN = 'search_open',
  SEARCH_CLOSE = 'search_close',
  SEARCH_SUBMIT = 'search_submit',
  SEARCH_RESULT_SEEN = 'search_result_seen',
  SEARCH_RESULT_CLICK = 'search_result_click',
  SEARCH_RECENT_CLICK = 'search_recent_click',
  ONBOARDING_OPEN = 'onboarding_open',
  ONBOARDING_DECLINE = 'onboarding_decline',
  ONBOARDING_SKIP = 'onboarding_skip',
  ONBOARDING_COMPLETE = 'onboarding_complete',
  CREDITS_INFO = 'credits_info',
}

// Screen type values
export enum ScreenType {
  GENERATOR = 'generator_screen',
  CHOOSER = 'chooser_screen',
  HISTORY = 'history',
  PREVIEW = 'preview_screen',
  PRESET_FEED = 'preset_feed',
  PROFILE = 'profile',
  CREDITS = 'credits',
  SEARCH = 'search',
  ONBOARDING = 'onboarding',
}

// Entry point for the preset feed open event
export type PresetFeedOpenSource = 'click' | 'deeplink';

// What caused a `credits_info` event
export type CreditsInfoTrigger = 'app_open' | 'preset_open' | 'login';

export enum ClickItem {
  Category = 'category',
  Effect = 'effect',
  Tool = 'tool',
  Edit = 'edit',
}

// Settings object for analytics
export interface AnalyticsSettings {
  category?: string;
  tool?: string;
  id?: string;
  error_message?: string;
  remaining_seconds?: number;
  day?: number;
  /** Normalized search term (`formatSearchKeyword`), on the search events. */
  keyword?: string;
  /** Hits returned for `keyword`; 0 means the query matched nothing. */
  results_count?: number;
  /** 1-based rank of the clicked result within the list it was clicked from. */
  position?: number;
  /** Categories liked by the end of onboarding; 0 means the deck taught us nothing. */
  liked_count?: number;
  /** Signed-in user's credits (subscription + add-on); `null` for a guest or before the balance loads. */
  credits_balance?: number | null;
  /** Price API quote for the preset, on `credits_info`; 0 for a free preset, `null` for a guest. */
  preset_credits?: number | null;
  /** What caused a `credits_info` event. */
  trigger?: CreditsInfoTrigger;
}

// Extended settings for failure cases (error fields required)
export interface AnalyticsSettingsWithError extends AnalyticsSettings {
  error_type: string;
  error_message: string;
}
